import { useEffect, useState } from 'react';
import { WorkspaceIcon } from '../icons';
import { ConnectionMark } from './connection-mark';
import { filterCounts, filterRows, type ConnectionFilter, type ConnectionRow } from './rows';
import { rowIdentity, type ConnectionWords } from './words';

const FILTERS: ConnectionFilter[] = ['all', 'mine', 'shared', 'attention'];

/**
 * Your connections: every account, server and database in one list, the ones that need you first.
 * A row opens its panel; a row that needs a sign-in also offers it in place.
 */
export function YourConnections({
  rows,
  loading,
  problems,
  words,
  busyKey,
  logoFor,
  onOpen,
  onSignIn,
  emptyText,
}: {
  rows: ConnectionRow[];
  loading: boolean;
  /** Said when nothing is connected; the page knows when connecting isn't possible here at all. */
  emptyText?: string;
  /** Sources that could not be read, each with its own retry. */
  problems: { key: string; message: string; onRetry: () => void }[];
  words: ConnectionWords;
  busyKey: string | null;
  logoFor: (slug: string | null) => string | null | undefined;
  onOpen: (row: ConnectionRow) => void;
  onSignIn?: (row: ConnectionRow) => (() => void) | null;
}) {
  const { copy } = words;
  const [filter, setFilter] = useState<ConnectionFilter>('all');
  const counts = filterCounts(rows);
  // A filter that empties out (the last sign-in was fixed) falls back to everything, and stays there.
  const active = counts[filter] || filter === 'all' ? filter : 'all';
  useEffect(() => {
    if (active !== filter) setFilter(active);
  }, [active, filter]);
  const visible = filterRows(rows, active);
  const labels: Record<ConnectionFilter, string> = {
    all: copy.filterAll,
    mine: copy.filterMine,
    shared: copy.filterShared,
    attention: copy.filterAttention,
  };
  return (
    <section className="ws-conn-section" aria-labelledby="ws-conn-yours-title">
      <div className="ws-conn-section-head">
        <div className="ws-conn-section-title">
          <h2 id="ws-conn-yours-title">{copy.yoursTitle}</h2>
          {!loading && <span>{rows.length}</span>}
        </div>
        {rows.length > 0 && (
          <div className="ws-conn-segment" role="group" aria-label={copy.filterLabel}>
            {FILTERS.filter((id) => id === 'all' || counts[id] > 0).map((id) => (
              <button
                key={id}
                type="button"
                aria-pressed={active === id}
                data-tone={id === 'attention' ? 'warn' : undefined}
                onClick={() => setFilter(id)}
              >
                {labels[id]}
                <span>{counts[id]}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {problems.map((problem) => (
        <div key={problem.key} className="ws-conn-problem" role="alert">
          <span>{problem.message}</span>
          <button type="button" className="ws-text-button" onClick={problem.onRetry}>
            {copy.retry}
          </button>
        </div>
      ))}
      {loading ? (
        <div className="ws-conn-list" role="status" aria-label={copy.yoursLoading}>
          {[0, 1, 2].map((index) => (
            <div key={index} className="ws-conn-row ws-conn-row-skeleton" aria-hidden="true">
              <span className="ws-conn-mark" />
              <span className="ws-skeleton" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="ws-conn-empty">{emptyText ?? copy.yoursEmpty}</p>
      ) : (
        <div className="ws-conn-list">
          <div className="ws-conn-list-head" aria-hidden="true">
            <span className="ws-conn-cell-main">{copy.columnConnection}</span>
            <span className="ws-conn-cell-who">{copy.columnWho}</span>
            <span className="ws-conn-cell-what">{copy.columnWhat}</span>
            <span className="ws-conn-cell-status">{copy.columnStatus}</span>
            <span className="ws-conn-cell-go" />
          </div>
          {visible.length ? (
            <ul aria-labelledby="ws-conn-yours-title">
              {visible.map((row) => (
                <ConnectionRowView
                  key={row.key}
                  row={row}
                  words={words}
                  busy={busyKey === row.key}
                  logo={logoFor(row.appSlug)}
                  onOpen={() => onOpen(row)}
                  onSignIn={row.state === 'attention' ? (onSignIn?.(row) ?? null) : null}
                />
              ))}
            </ul>
          ) : (
            <p className="ws-conn-empty">{copy.filterEmpty}</p>
          )}
        </div>
      )}
    </section>
  );
}

function ConnectionRowView({
  row,
  words,
  busy,
  logo,
  onOpen,
  onSignIn,
}: {
  row: ConnectionRow;
  words: ConnectionWords;
  busy: boolean;
  logo: string | null | undefined;
  onOpen: () => void;
  onSignIn: (() => void) | null;
}) {
  const { copy } = words;
  const identity = rowIdentity(row, words);
  return (
    <li className="ws-conn-row" data-state={row.state}>
      <button type="button" className="ws-conn-row-open ws-conn-cell-main" onClick={onOpen}>
        <ConnectionMark
          name={row.name}
          src={row.appSlug ? logo : null}
          logoId={row.logoId}
          glyph={row.kind === 'server' && row.logoId === 'custom-mcp' ? 'source' : undefined}
          state={row.state}
        />
        <span className="ws-conn-name">
          <strong>{row.name}</strong>
          {identity && <span>{identity}</span>}
        </span>
      </button>
      <span className="ws-conn-cell-who ws-conn-meta">
        <WorkspaceIcon
          name={
            row.audience === 'private' ? 'person' : row.audience === 'company' ? 'company' : 'users'
          }
          size={15}
        />
        {words.who(row)}
      </span>
      <span className="ws-conn-cell-what ws-conn-meta">
        {row.changes !== 'unknown' && (
          <WorkspaceIcon name={row.changes === 'write' ? 'edit' : 'eye'} size={15} />
        )}
        {words.what(row)}
      </span>
      <span className="ws-conn-cell-status">
        {busy ? (
          // Signing in again is a connect; a test or a disconnect keeps the state it has.
          <span className="ws-conn-state" data-state="busy" aria-busy="true">
            {row.state === 'attention' ? copy.stateConnecting : words.state(row.state)}
          </span>
        ) : onSignIn ? (
          <button type="button" className="ws-conn-btn" data-tone="warn" onClick={onSignIn}>
            {copy.signInAgain}
          </button>
        ) : (
          <span className="ws-conn-state" data-state={row.state}>
            {words.state(row.state)}
          </span>
        )}
      </span>
      <span className="ws-conn-cell-go" aria-hidden="true">
        <WorkspaceIcon name="chevron" size={15} />
      </span>
    </li>
  );
}
